import { useEffect, useMemo, useRef, useState } from "react";
import type { HandsData } from "../api/types";

// A horizontal hand-activity strip aligned under the video. Each sampled frame becomes a
// thin vertical cell coloured by how many hands were present (0 / 1 / 2+). Click anywhere
// on the strip to seek the video to that time. A playhead tracks currentTime.
//
// Rendered to a <canvas> (one cell per pixel-ish column) so thousands of frames stay cheap.

const COLOR_0 = "#1b2230"; // no hands
const COLOR_1 = "#3b6fb0"; // one hand
const COLOR_2 = "#22d3ee"; // both hands

interface Props {
  videoRef: React.RefObject<HTMLVideoElement>;
  hands: HandsData;
  durationS: number | null;
}

export default function HandTimeline({ videoRef, hands, durationS }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<{ x: number; t: number; n: number } | null>(null);
  const playheadRef = useRef<HTMLDivElement>(null);

  // Total span: prefer the real video duration, else the last sampled frame time.
  const span = useMemo(() => {
    if (durationS && durationS > 0) return durationS;
    const last = hands.frames[hands.frames.length - 1];
    return last ? last.t : 0;
  }, [durationS, hands]);

  // Per-frame hand counts sorted by time (cheap binary lookup for hover).
  const points = useMemo(() => {
    return [...hands.frames]
      .map((f) => ({ t: f.t, n: f.hands.length }))
      .sort((a, b) => a.t - b.t);
  }, [hands]);

  // Draw the heatmap. Re-render on resize via ResizeObserver.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || span <= 0) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const render = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cw = rect.width;
      const ch = rect.height;
      ctx.clearRect(0, 0, cw, ch);
      ctx.fillStyle = COLOR_0;
      ctx.fillRect(0, 0, cw, ch);

      // Bin frames into pixel columns; keep the max hand-count per column.
      const cols = Math.max(1, Math.floor(cw));
      const binMax = new Int8Array(cols).fill(-1);
      for (const p of points) {
        const x = Math.min(cols - 1, Math.floor((p.t / span) * cols));
        if (p.n > binMax[x]) binMax[x] = p.n;
      }
      for (let x = 0; x < cols; x++) {
        const n = binMax[x];
        if (n < 0) continue; // no sample in this column
        ctx.fillStyle = n >= 2 ? COLOR_2 : n === 1 ? COLOR_1 : COLOR_0;
        ctx.fillRect(x, 0, 1, ch);
      }
    };

    render();
    const ro = new ResizeObserver(render);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [points, span]);

  // Playhead follows the video time.
  useEffect(() => {
    const video = videoRef.current;
    const ph = playheadRef.current;
    if (!video || !ph || span <= 0) return;
    let raf = 0;
    const tick = () => {
      const pct = Math.max(0, Math.min(1, video.currentTime / span));
      ph.style.left = `${pct * 100}%`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoRef, span]);

  if (span <= 0 || points.length === 0) return null;

  const nearestN = (t: number): number => {
    // binary search nearest point time
    let lo = 0;
    let hi = points.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (points[mid].t < t) lo = mid + 1;
      else hi = mid;
    }
    let best = points[lo];
    if (lo > 0 && Math.abs(points[lo - 1].t - t) < Math.abs(best.t - t)) best = points[lo - 1];
    return best.n;
  };

  const seekTo = (clientX: number, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const t = pct * span;
    const v = videoRef.current;
    if (v) v.currentTime = t;
    return t;
  };

  return (
    <div className="hand-timeline">
      <div className="hand-timeline-head">
        <span className="hand-timeline-title">Hand activity</span>
        <span className="hand-timeline-legend">
          <span className="htl-key">
            <span className="htl-swatch" style={{ background: COLOR_0 }} /> none
          </span>
          <span className="htl-key">
            <span className="htl-swatch" style={{ background: COLOR_1 }} /> 1 hand
          </span>
          <span className="htl-key">
            <span className="htl-swatch" style={{ background: COLOR_2 }} /> 2 hands
          </span>
        </span>
      </div>
      <div
        className="hand-timeline-track"
        onClick={(e) => seekTo(e.clientX, e.currentTarget)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
          const t = pct * span;
          setHover({ x: e.clientX - rect.left, t, n: nearestN(t) });
        }}
        onMouseLeave={() => setHover(null)}
        role="slider"
        aria-label="Hand activity timeline — click to seek"
        aria-valuemin={0}
        aria-valuemax={Math.round(span)}
        aria-valuenow={hover ? Math.round(hover.t) : 0}
        tabIndex={0}
      >
        <canvas ref={canvasRef} className="hand-timeline-canvas" />
        <div ref={playheadRef} className="hand-timeline-playhead" />
        {hover && (
          <div className="hand-timeline-tip" style={{ left: hover.x }}>
            {hover.t.toFixed(1)}s · {hover.n === 0 ? "no hands" : `${hover.n} hand${hover.n > 1 ? "s" : ""}`}
          </div>
        )}
      </div>
    </div>
  );
}
